/**
 * ScratchyGame client: funds, session-key authorisation, and relayed play.
 *
 *   1. deposit()  — ONE wallet signature. Moves funds into the game's internal
 *                   ledger AND authorises the browser session key in the same
 *                   transaction, so onboarding is a single confirmation.
 *   2. play()     — signs a permit with the session key and POSTs it to the
 *                   relayer. No wallet popup, no gas, one round trip. The
 *                   relayer submits and the play settles atomically on-chain.
 *   3. withdraw() — a wallet transaction again, because moving money OUT should
 *                   always be an explicit, wallet-confirmed act.
 *
 * The outcome comes back from the chain. Nothing here decides a prize.
 */
import { BrowserProvider, Contract, JsonRpcProvider, Wallet, parseUnits } from "ethers";
import { CHAIN_CONFIG, SETTLEMENT, NATIVE_ADDRESS, unitsToWei, weiToUnits, formatWei } from "./config";
import { signPlayPermit, type PermitParams, type PlayParams } from "./permit";
import { pendingCode, clearPendingCode } from "./referral";
import { newPlayerSeed, sessionKeyFor } from "./session";

export const GAME_ABI = [
  "function deposit(address currency, uint256 amount, address delegate, uint128 allowanceAmount, uint64 allowanceDeadline) payable",
  "function withdraw(address currency, uint256 amount)",
  "function setAllowance(address currency, address delegate, uint128 amount, uint64 deadline)",
  "function balanceOf(address currency, address account) view returns (uint256)",
  "function allowance(address currency, address account, address delegate) view returns (uint128 amount, uint64 deadline)",
  "function getTier(uint8 tierId) view returns (uint256 price, uint256 topPrize, bool active, uint32[] cumProbPpm, uint256[] prizes)",
  "function bankroll(address currency) view returns (uint256)",
  "function paused() view returns (bool)"
];

export const ERC20_ABI = [
  "function approve(address spender, uint256 amount) returns (bool)",
  "function allowance(address owner, address spender) view returns (uint256)",
  "function decimals() view returns (uint8)",
  "function balanceOf(address) view returns (uint256)"
];

export interface ResolvedCard {
  index: number;
  /** 0..999999 — the raw draw, so the UI can show the number the chain saw. */
  roll: number;
  prize: bigint;
}

export interface PlayResult {
  txHash: string;
  stake: bigint;
  payout: bigint;
  /** payout expressed in display units, for the grid and banner. */
  payoutUnits: number;
  cards: ResolvedCard[];
  playerSeed: string;
  externalRandomness: string;
  randomness: string;
}

export class ChainError extends Error {}

/**
 * Turn a wallet/node failure into something a player can act on.
 *
 * ethers surfaces "missing revert data" for a call that failed without a
 * reason string — most often because the wallet cannot cover value + gas. Shown
 * raw it tells the player nothing and looks like a bug in the game.
 */
export function explainChainError(e: any): string {
  const raw = String(e?.shortMessage || e?.message || e || "");
  const code = e?.code;

  if (code === "ACTION_REJECTED" || /user (rejected|denied)/i.test(raw)) {
    return "Cancelled in your wallet.";
  }
  if (/insufficient funds/i.test(raw)) {
    return `Not enough ${SETTLEMENT.symbol} in your wallet to cover the amount plus gas.`;
  }
  if (/missing revert data|CALL_EXCEPTION|cannot estimate gas/i.test(raw)) {
    return `The transaction could not be prepared — usually not enough ${SETTLEMENT.symbol} for gas on ${CHAIN_CONFIG.chainName}.`;
  }
  if (/network|timeout|failed to fetch/i.test(raw)) {
    return "Network problem reaching the chain. Try again in a moment.";
  }
  /* Anything unrecognised: keep it short rather than dumping a stack. */
  return raw.split("\n")[0].slice(0, 120) || "Transaction failed.";
}

/**
 * What the transaction fee will actually cost, in wei.
 *
 * This used to be a flat 0.002 ETH "generous buffer". On this chain gas runs
 * about 0.02 gwei and a deposit about 150k gas — roughly 6,000 gwei, so the
 * flat figure was ~330x the real cost. Anyone holding less than 0.002 ETH was
 * told they could not afford a deposit they could comfortably afford: a wallet
 * with 100,000 gwei was refused a 10,000 gwei deposit, because the buffer alone
 * was 20x the whole balance.
 *
 * Reading live fee data costs one RPC call and cannot drift like a constant.
 * The doubling covers a fee move between this check and the wallet sending.
 */
async function feeCostWei(gasLimit: bigint): Promise<bigint> {
  const fee = await readProvider().getFeeData();
  const price = fee.maxFeePerGas ?? fee.gasPrice ?? 0n;
  return price * gasLimit * 2n;
}

/**
 * Check the wallet can actually pay before prompting.
 *
 * Catching this up front turns an opaque post-signature failure into a
 * specific, checkable statement. `gasLimit` should be a real estimate of the
 * transaction being sent; the default is a generous ceiling for a deposit.
 */
export async function assertCanAfford(account: string, units: number, gasLimit = 300_000n): Promise<void> {
  const need = unitsToWei(units);
  const sym = SETTLEMENT.display.symbol;

  /* An ERC-20 asset is held in the token, while gas is paid in ETH — two
     separate balances, and a player can easily have one and not the other. */
  if (!SETTLEMENT.isNative) {
    const token = new Contract(SETTLEMENT.address, ERC20_ABI, readProvider());
    const held: bigint = await token.balanceOf(account);
    if (held < need) {
      throw new ChainError(
        `This wallet holds ${formatWei(held)} ${sym} on ${CHAIN_CONFIG.chainName}, ` +
          `but the deposit is ${formatWei(need)} ${sym}. Send some ${SETTLEMENT.symbol} to it first.`
      );
    }
    const gas = await feeCostWei(gasLimit);
    const eth = await readProvider().getBalance(account);
    if (eth < gas) {
      throw new ChainError(
        `Not enough ETH for the transaction fee. ${SETTLEMENT.symbol} deposits still cost gas in ETH.`
      );
    }
    return;
  }

  const [bal, fee] = await Promise.all([readProvider().getBalance(account), feeCostWei(gasLimit)]);

  if (bal < need) {
    throw new ChainError(
      `This wallet holds ${formatWei(bal)} ${sym} on ${CHAIN_CONFIG.chainName}, ` +
        `but the deposit is ${formatWei(need)} ${sym}. Send some ${SETTLEMENT.symbol} to it first.`
    );
  }
  if (bal < need + fee) {
    /* Name the shortfall. "Leave a little spare" next to two numbers that look
       fine tells the player nothing about what is actually wrong. */
    throw new ChainError(
      `Not enough for the transaction fee: this wallet holds ${formatWei(bal)} ${sym}, ` +
        `the deposit is ${formatWei(need)} ${sym} and the fee is about ${formatWei(fee)} ${sym} — ` +
        `about ${formatWei(need + fee - bal)} ${sym} short. Try a smaller amount.`
    );
  }
}

/**
 * One read provider for the page.
 *
 * This used to construct a fresh JsonRpcProvider on every call, and ethers
 * detects the network on each new instance — an eth_chainId round trip before
 * the actual read. Every balance refresh was two calls, ~300ms each from a
 * browser, for one number. Shared and pinned to a static network, the
 * detection happens once per page load and never again. Exported so the wins
 * feed does not build its own.
 */
let shared: JsonRpcProvider | null = null;
export function readProvider(): JsonRpcProvider {
  if (!shared) shared = new JsonRpcProvider(CHAIN_CONFIG.rpcUrl, undefined, { staticNetwork: true });
  return shared;
}

/**
 * Provider for history — log queries spanning thousands of blocks.
 *
 * Separate because the two endpoints can do different things, not because one
 * is better. The live endpoint is chosen for a current view of the head and
 * refuses wide eth_getLogs ranges outright; the chain's own RPC serves those
 * but lags the head. Asking each for the thing it can actually answer is the
 * whole of it. Memoised for the same reason as above.
 */
let archive: JsonRpcProvider | null = null;
export function archiveProvider(): JsonRpcProvider {
  if (!archive) archive = new JsonRpcProvider(CHAIN_CONFIG.archiveRpcUrl, undefined, { staticNetwork: true });
  return archive;
}

function gameRead(): Contract {
  return new Contract(CHAIN_CONFIG.gameAddress, GAME_ABI, readProvider());
}

/* ------------------------------------------------------------------ reads */

/** Player's in-game balance, in display units. */
export async function gameBalanceUnits(account: string): Promise<number> {
  const wei: bigint = await gameRead().balanceOf(SETTLEMENT.address, account);
  return weiToUnits(wei);
}

/** Remaining session-key authority. The UI uses this to prompt a re-auth. */
export async function sessionAllowance(account: string): Promise<{ units: number; deadline: bigint }> {
  const key = sessionKeyFor(account);
  const [amount, deadline] = await gameRead().allowance(SETTLEMENT.address, account, key.address);
  return { units: weiToUnits(amount), deadline };
}

export async function tierPriceUnits(tierId: number): Promise<number> {
  const [price] = await gameRead().getTier(tierId);
  return weiToUnits(price);
}

/* ----------------------------------------------------------------- writes */

/**
 * Deposit and authorise the session key in one transaction.
 *
 * `allowanceUnits` bounds what a stolen session key could ever spend, so it
 * defaults to the deposit rather than something unbounded. The player
 * re-authorises when it runs out; that is a cheap prompt, not a failure.
 */
export async function depositAndAuthorise(
  eip1193: any,
  units: number,
  allowanceUnits = units,
  allowanceDays = 7
): Promise<string> {
  const provider = new BrowserProvider(eip1193);
  const signer = await provider.getSigner();
  const account = await signer.getAddress();

  const amount = unitsToWei(units);
  const allowanceAmount = unitsToWei(allowanceUnits);
  const deadline = BigInt(Math.floor(Date.now() / 1000) + allowanceDays * 86400);

  /* An ERC-20 settlement asset needs a prior approve; native ETH rides along
     as msg.value on the deposit itself, which is one fewer confirmation. */
  if (!SETTLEMENT.isNative) {
    const token = new Contract(SETTLEMENT.address, ERC20_ABI, signer);
    const current: bigint = await token.allowance(account, CHAIN_CONFIG.gameAddress);
    if (current < amount) {
      await (await token.approve(CHAIN_CONFIG.gameAddress, amount)).wait();
    }
  }

  const key = sessionKeyFor(account);
  const game = new Contract(CHAIN_CONFIG.gameAddress, GAME_ABI, signer);
  const args = [SETTLEMENT.address, amount, key.address, allowanceAmount, deadline] as const;
  const overrides = SETTLEMENT.isNative ? { value: amount } : {};

  /* Fail with a reason the player can act on, before the wallet prompt.
     Estimating the real call rather than assuming a limit: it prices this
     deposit rather than a typical one, and a revert surfaces here with its
     reason instead of as an opaque rejection after the player has signed. */
  {
    let gasLimit = 300_000n;
    try {
      gasLimit = await game.deposit.estimateGas(...args, overrides);
    } catch (e) {
      /* Estimation needs the balance it is about to check, so a failure here
         is usually the very shortfall assertCanAfford is about to name.
         Fall through to the ceiling and let it produce the better message. */
    }
    await assertCanAfford(account, units, gasLimit);
  }

  const tx = await game.deposit(...args, overrides);
  await tx.wait();
  return tx.hash;
}

/** Top up the session key without moving funds. */
export async function reauthoriseSession(eip1193: any, allowanceUnits: number, allowanceDays = 7): Promise<string> {
  const provider = new BrowserProvider(eip1193);
  const signer = await provider.getSigner();
  const account = await signer.getAddress();
  const key = sessionKeyFor(account);
  const game = new Contract(CHAIN_CONFIG.gameAddress, GAME_ABI, signer);
  const tx = await game.setAllowance(
    SETTLEMENT.address,
    key.address,
    unitsToWei(allowanceUnits),
    BigInt(Math.floor(Date.now() / 1000) + allowanceDays * 86400)
  );
  await tx.wait();
  return tx.hash;
}

/** Revoke immediately. Wallet-confirmed, because it is a security action. */
export async function revokeSession(eip1193: any): Promise<string> {
  const provider = new BrowserProvider(eip1193);
  const signer = await provider.getSigner();
  const account = await signer.getAddress();
  const key = sessionKeyFor(account);
  const game = new Contract(CHAIN_CONFIG.gameAddress, GAME_ABI, signer);
  const tx = await game.setAllowance(SETTLEMENT.address, key.address, 0n, 0n);
  await tx.wait();
  return tx.hash;
}

export async function withdraw(eip1193: any, units: number): Promise<string> {
  const provider = new BrowserProvider(eip1193);
  const signer = await provider.getSigner();
  const game = new Contract(CHAIN_CONFIG.gameAddress, GAME_ABI, signer);
  const tx = await game.withdraw(SETTLEMENT.address, unitsToWei(units));
  await tx.wait();
  return tx.hash;
}

/* ------------------------------------------------------------------- play */

/**
 * Buy and resolve `count` cards.
 *
 * Signs locally with the session key, hands the permit to the relayer, and
 * returns what the chain decided. No wallet interaction on this path at all.
 */
export async function play(account: string, tierId: number, count = 1): Promise<PlayResult> {
  const key: Wallet = sessionKeyFor(account);

  const params: PlayParams = {
    currency: SETTLEMENT.address,
    owner: account,
    tierId,
    count,
    playerSeed: newPlayerSeed()
  };
  const permit: PermitParams = {
    delegate: key.address,
    // Short-lived on purpose: a leaked permit is only useful for a minute, and
    // the relayer is the only address that can submit it anyway.
    deadline: BigInt(Math.floor(Date.now() / 1000) + 120)
  };

  const signature = await signPlayPermit(key, CHAIN_CONFIG.chainIdNumber, CHAIN_CONFIG.gameAddress, params, permit);

  const res = await fetch(`${CHAIN_CONFIG.relayerUrl}/play`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      params,
      permit: { delegate: permit.delegate, deadline: permit.deadline.toString() },
      signature,
      /* Attribution happens at the first PLAY, not on arrival: a code is only
         worth binding once there is real activity behind it, and the relayer
         is the only party that can see both sides of the pair. Sent until it
         sticks, then cleared. */
      ...(pendingCode() ? { referralCode: pendingCode() } : {})
    })
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}) as any);
    throw new ChainError(body.error || `relayer returned ${res.status}`);
  }

  const j = await res.json();
  /* The play landed, so any inbound code has now been offered to the relayer.
     Binding is first-touch and idempotent there, so one successful play is
     enough — resending it forever would just be noise. */
  if (pendingCode()) clearPendingCode();
  const payout = BigInt(j.payout);
  return {
    txHash: j.txHash,
    stake: BigInt(j.stake),
    payout,
    payoutUnits: weiToUnits(payout),
    cards: (j.cards ?? []).map((c: any) => ({ index: c.index, roll: c.roll, prize: BigInt(c.prize) })),
    playerSeed: j.playerSeed,
    externalRandomness: j.externalRandomness,
    randomness: j.randomness
  };
}

/* --------------------------------------------------------------- legacy */

/**
 * Balance still held in the contract this one replaced.
 *
 * Ten addresses had a balance in the old ScratchyGame when the multi-currency
 * contract went live, and only one of them was ours — the rest found the
 * public URL and played. Their funds are not lost: withdraw() has no pause
 * modifier and the old contract still holds exactly what it owes. But nothing
 * would ever have told them, and the game they return to now points at a
 * different address where their balance reads zero.
 *
 * Always denominated in ETH wei: the old contract only ever settled native
 * ETH, so this deliberately does NOT go through SETTLEMENT, which now follows
 * whichever asset the player has selected.
 */
export async function legacyBalanceWei(account: string): Promise<bigint> {
  if (!CHAIN_CONFIG.legacyGameAddress) return 0n;
  try {
    const old = new Contract(CHAIN_CONFIG.legacyGameAddress, GAME_ABI, readProvider());
    return (await old.balanceOf(NATIVE_ADDRESS, account)) as bigint;
  } catch (e) {
    /* Advisory only — a returning player must never be blocked by this. */
    return 0n;
  }
}

/** Move a legacy balance out of the old contract, back to the player's wallet. */
export async function withdrawLegacy(eip1193: any, amountWei: bigint): Promise<string> {
  const provider = new BrowserProvider(eip1193);
  const signer = await provider.getSigner();
  const old = new Contract(CHAIN_CONFIG.legacyGameAddress, GAME_ABI, signer);
  const tx = await old.withdraw(NATIVE_ADDRESS, amountWei);
  await tx.wait();
  return tx.hash;
}
